import { store } from "../store";
import { deepFreeze } from "../util/misc";
import DataStructure from "./DataStructure";
import { compact, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const ADDRESS_TYPES = {
  billing: { label: "Billing", icon: "billing" },
  home: { label: "Home", icon: "home" },
  work: { label: "Work", icon: "work" },
};
deepFreeze(ADDRESS_TYPES);

// Required fields:
// zip
// state
// city
// == AND ==
// street
// == OR ==
// lot
// development

// Optional fields:
// contact
// country
// lat
// lng
// type

export default class Address extends DataStructure {
  constructor(
    data = {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
      type: "",
    }
  ) {
    super(data);
  }

  // Editing

  get isValid() {
    return Boolean(
      this.data.zip &&
        this.data.state &&
        this.data.city &&
        (this.data.street || (this.data.lot && this.data.development))
    );
  }

  isEqual(address) {
    return isEmpty(diff(this.data, address.data));
  }

  locationKey() {
    const normalize = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();
    return [
      normalize(this.data.street),
      normalize(this.data.development),
      normalize(this.data.lot),
      normalize(this.data.city),
      normalize(this.data.state),
      normalize(this.data.zip),
    ].join("|");
  }

  isSameLocation(address) {
    if (!address) return false;
    const other = address instanceof Address ? address : new Address(address);
    return this.locationKey() === other.locationKey();
  }

  // Formatting

  get type() {
    return this.data.type
      ? ADDRESS_TYPES[this.data.type] || {
          label: this.data.type,
          icon: "",
        }
      : null;
  }

  get link() {
    let gpsLink = this.gpsLink;
    return (
      gpsLink ||
      "https://maps.google.com/maps?q=" +
        encodeURIComponent(
          compact([
            this.data.street || this.data.development,
            this.data.city,
            this.data.state,
            this.data.zip,
            this.data.country,
          ]).join(", ")
        ).replace(/%20/g, "+")
    );
  }

  get gpsLink() {
    if (!(this.lat && this.lng)) {
      return "";
    }

    return `https://maps.google.com/maps?q=${this.data.lat},${this.data.lng}`;
  }

  get contact() {
    return this.data.contact
      ? store.state.main.contacts[this.data.contact] || null
      : null;
  }

  toString(
    options = { inline: false, html: false, noName: false, streetOnly: false }
  ) {
    const parts = [];
    const separator = options.inline ? ", " : options.html ? "<br/>" : "\n";
    const contacts = store.state.main.contacts;

    if (!options.noName) {
      if (this.contact) {
        if (this.contact.name) {
          parts.push(this.contact.name);
        } else if (this.contact in contacts) {
          parts.push(contacts[this.contact].name);
        }
      } else if (this.name) {
        parts.push(this.name);
      }
    }

    if (this.street) {
      parts.push(this.street);
    } else {
      if (this.development) {
        parts.push(this.development);
      }
      if (this.lot) {
        parts.push(`Lot ${this.lot}`);
      }
    }

    if (!options.streetOnly) {
      parts.push(
        compact([this.city, this.state]).join(", ") +
          (this.zip ? " " + this.zip : "")
      );
    }

    return compact(parts).join(separator);
  }
}
